/**
 * Sponsor budget XLSX parser.
 *
 * Tolerant of the common sponsor template (e.g. SMT19969/C004 layout):
 *   Header rows: Sponsor / Protocol / Title / Version / Project / Country / Currency / Site / PI
 *   Grid section starts where a row begins with "Trial Procedure"
 *   Sub-totals on rows beginning with "Sub Total", "Overhead on", "Total"
 *
 * Returns a structured object the BudgetImportDialog can preview and persist.
 * No assumptions: visit columns are emitted exactly as the file labels them.
 */
import * as XLSX from "xlsx";

export type FeeCategory = "procedure" | "non_procedure" | "patient_stipend" | "ad_hoc";

export interface ParsedFeeLine {
  name: string;
  unitCost: number;
  category: FeeCategory;
  /** map of visit column label -> quantity (numeric only; "X" / blanks dropped) */
  visitQuantities: Record<string, number>;
  rawRow: number;
}

export interface ParsedBudget {
  sheetName: string;
  protocolNumber: string | null;
  protocolTitle: string | null;
  protocolVersion: string | null;
  project: string | null;
  country: string | null;
  currency: string;
  siteName: string | null;
  principalInvestigator: string | null;
  visitColumns: string[];
  fees: ParsedFeeLine[];
  /** Per-visit total row from the file (if present) */
  perVisitTotals: Record<string, number>;
  overheadPct: number | null;
  /** Additional ad-hoc treatment-related costs (no visit grid) */
  adHoc: { name: string; amount: number }[];
}

const HEADER_KEYS: Record<string, keyof ParsedBudget> = {
  "trial protocol:": "protocolNumber",
  "title of the trial:": "protocolTitle",
  "protocol version:": "protocolVersion",
  "project:": "project",
  "country:": "country",
  "currency:": "currency",
  "trial site:": "siteName",
  "principal investigator:": "principalInvestigator",
};

function asString(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  return String(v).trim() || null;
}
function asNumber(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export async function parseBudgetXlsx(file: File): Promise<ParsedBudget> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const sheetName = wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  const rows: unknown[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: true });

  const out: ParsedBudget = {
    sheetName,
    protocolNumber: null,
    protocolTitle: null,
    protocolVersion: null,
    project: null,
    country: null,
    currency: "USD",
    siteName: null,
    principalInvestigator: null,
    visitColumns: [],
    fees: [],
    perVisitTotals: {},
    overheadPct: null,
    adHoc: [],
  };

  // 1. Header scan (first ~12 rows usually)
  for (let i = 0; i < Math.min(rows.length, 15); i++) {
    const row = rows[i] || [];
    for (let c = 0; c < row.length - 1; c++) {
      const key = asString(row[c])?.toLowerCase();
      if (key && HEADER_KEYS[key]) {
        const val = asString(row[c + 1]);
        if (val) (out as any)[HEADER_KEYS[key]] = val;
      }
    }
  }

  // 2. Find the visit-header row: contains "Trial Procedure" + "Unit Cost"
  let visitHeaderRow = -1;
  let unitCostCol = -1;
  let nameCol = -1;
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i] || [];
    const idxName = row.findIndex(c => asString(c)?.toLowerCase() === "trial procedure");
    const idxCost = row.findIndex(c => asString(c)?.toLowerCase() === "unit cost");
    if (idxName >= 0 && idxCost >= 0) {
      visitHeaderRow = i;
      nameCol = idxName;
      unitCostCol = idxCost;
      break;
    }
  }
  if (visitHeaderRow < 0) {
    throw new Error("Could not find 'Trial Procedure' / 'Unit Cost' header row.");
  }

  // 3. Visit columns = everything to the right of Unit Cost, excluding the TOTAL column.
  // Sponsor templates often have 1-2 sub-header rows; concatenate up to 3 rows of labels.
  const labelRows = [
    rows[visitHeaderRow] || [],
    rows[visitHeaderRow + 1] || [],
    rows[visitHeaderRow + 2] || [],
  ];
  const colCount = Math.max(...labelRows.map(r => r.length));
  const totalCol = labelRows[0].findIndex(c => asString(c)?.toLowerCase() === "total");
  const visitCols: { col: number; label: string }[] = [];
  for (let c = unitCostCol + 1; c < colCount; c++) {
    if (c === totalCol) continue;
    const parts = labelRows
      .map(r => asString(r[c]))
      .filter((x): x is string => !!x && x.toLowerCase() !== "total");
    const label = parts.join(" — ").replace(/\s+/g, " ").trim();
    if (label) visitCols.push({ col: c, label });
  }
  out.visitColumns = visitCols.map(v => v.label);

  // 4. Iterate fee rows. Track current category by section markers.
  let currentCategory: FeeCategory = "procedure";
  let inAdHoc = false;
  for (let i = visitHeaderRow + 3; i < rows.length; i++) {
    const row = rows[i] || [];
    const name = asString(row[nameCol]);
    if (!name) continue;
    const lname = name.toLowerCase();

    // Section dividers
    if (lname.startsWith("procedure costs")) { currentCategory = "procedure"; continue; }
    if (lname.startsWith("non-procedure fees")) { currentCategory = "non_procedure"; continue; }
    if (lname.startsWith("additional treatment related")) { inAdHoc = true; continue; }

    // Sub-totals / overhead
    if (lname.startsWith("overhead on")) {
      const pct = asNumber(row[unitCostCol]);
      if (pct !== null && out.overheadPct === null) out.overheadPct = pct;
      continue;
    }
    if (lname.startsWith("sub total") || lname.startsWith("total ")) {
      // Capture per-visit totals from the "Total Per Visit Costs" row
      if (lname.includes("per visit")) {
        for (const v of visitCols) {
          const n = asNumber(row[v.col]);
          if (n !== null) out.perVisitTotals[v.label] = n;
        }
      }
      continue;
    }

    const unitCost = asNumber(row[unitCostCol]);

    // Ad-hoc section: just name + amount in unit-cost column
    if (inAdHoc) {
      if (unitCost !== null) out.adHoc.push({ name, amount: unitCost });
      continue;
    }

    if (unitCost === null) continue;

    // Detect the patient stipend row by name
    let cat: FeeCategory = currentCategory;
    if (currentCategory === "non_procedure" && /reimburs|stipend|subject\s+payment/i.test(name)) {
      cat = "patient_stipend";
    }

    const visitQuantities: Record<string, number> = {};
    for (const v of visitCols) {
      const n = asNumber(row[v.col]);
      if (n !== null && n !== 0) visitQuantities[v.label] = n;
    }

    out.fees.push({
      name,
      unitCost,
      category: cat,
      visitQuantities,
      rawRow: i + 1,
    });
  }

  return out;
}
